/**
 * THE DETAIL SUPPLY CO — THEME JAVASCRIPT
 * Minimal, vanilla JS. No dependencies. Performance-first.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     HEADER: Scroll detection + section contrast
  ───────────────────────────────────────────── */
  const header = document.querySelector('.site-header');

  if (header) {
    // Header contrast via IntersectionObserver on data-nav sections
    const lightSections = document.querySelectorAll('[data-nav="light"]');

    if (lightSections.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              header.classList.add('header--light');
            } else {
              header.classList.remove('header--light');
            }
          });
        },
        {
          root: null,
          rootMargin: `-${parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h') || '80')}px 0px 0px 0px`,
          threshold: 0,
        }
      );

      lightSections.forEach((section) => observer.observe(section));
    }

    // Mobile menu toggle
    const menuToggle = document.querySelector('.header__menu-toggle');
    const mobileNav = document.querySelector('.header__mobile-nav');

    if (menuToggle && mobileNav) {
      menuToggle.addEventListener('click', () => {
        const isOpen = menuToggle.classList.toggle('is-open');
        mobileNav.classList.toggle('is-open', isOpen);
        document.body.style.overflow = isOpen ? 'hidden' : '';
      });

      // Close on nav link click
      mobileNav.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          menuToggle.classList.remove('is-open');
          mobileNav.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      });
    }
  }

  /* ─────────────────────────────────────────────
     HERO PARALLAX
  ───────────────────────────────────────────── */
  const heroBg = document.querySelector('.hero-cinematic__bg img');

  if (heroBg) {
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY;
          const hero = document.querySelector('.hero-cinematic');

          if (hero) {
            const heroH = hero.offsetHeight;
            if (scrolled < heroH) {
              const translateY = scrolled * 0.3;
              heroBg.style.transform = `scale(1.1) translateY(${translateY}px)`;
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────
     CART: AJAX Add to Cart
  ───────────────────────────────────────────── */
  function showCartNotification(text) {
    const notification = document.getElementById('cart-notification');
    const notifText = document.getElementById('cart-notification-text');

    if (!notification || !notifText) return;

    notifText.textContent = text;
    notification.classList.add('is-visible');

    setTimeout(() => {
      notification.classList.remove('is-visible');
    }, 3000);
  }

  function updateCartCount(count) {
    const counts = document.querySelectorAll('.cart-count');
    counts.forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
  }

  function addToCart(variantId, quantity, btn) {
    if (!variantId) return;

    const originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.textContent = 'ADDING...';
      btn.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          showCartNotification(data.description || 'Unable to add item');
        } else {
          showCartNotification(`${data.product_title} — ADDED TO CART`);
          // Fetch cart to update count
          fetch('/cart.js')
            .then((r) => r.json())
            .then((cart) => updateCartCount(cart.item_count));
        }
      })
      .catch(() => {
        showCartNotification('ERROR — PLEASE TRY AGAIN');
      })
      .finally(() => {
        if (btn) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
  }

  // Quick add buttons on product cards
  document.querySelectorAll('[data-quick-add]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      addToCart(variantId, 1, btn);
    });
  });

  // Main product form add to cart
  const productForm = document.getElementById('product-form');

  if (productForm) {
    productForm.addEventListener('submit', (e) => {
      e.preventDefault();

      const formData = new FormData(productForm);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1', 10);
      const submitBtn = productForm.querySelector('[type="submit"]');

      addToCart(variantId, quantity, submitBtn);
    });
  }

  /* ─────────────────────────────────────────────
     PRODUCT GALLERY
  ───────────────────────────────────────────── */
  const galleryMain = document.querySelector('.product-gallery__main img');
  const thumbs = document.querySelectorAll('.product-gallery__thumb');

  if (galleryMain && thumbs.length) {
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;

        galleryMain.style.opacity = '0';
        setTimeout(() => {
          if (src) galleryMain.src = src;
          if (srcset) galleryMain.srcset = srcset;
          galleryMain.style.opacity = '1';
        }, 150);

        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ─────────────────────────────────────────────
     PRODUCT VARIANT SELECTOR
  ───────────────────────────────────────────── */
  const variantOptions = document.querySelectorAll('.variant-option');
  const variantInput = document.getElementById('variant-id-input');

  if (variantOptions.length && window.__product_variants) {
    variantOptions.forEach((opt) => {
      opt.addEventListener('click', () => {
        const optionName = opt.dataset.optionName;
        const optionValue = opt.dataset.optionValue;

        // Mark active within same option group
        document.querySelectorAll(`[data-option-name="${optionName}"]`).forEach((o) => {
          o.classList.remove('is-active');
        });
        opt.classList.add('is-active');

        // Find matching variant
        const selectedOptions = {};
        document.querySelectorAll('.variant-option.is-active').forEach((active) => {
          selectedOptions[active.dataset.optionName] = active.dataset.optionValue;
        });

        const matched = window.__product_variants.find((v) => {
          return v.options.every((val, idx) => {
            const key = `option${idx + 1}`;
            return Object.values(selectedOptions)[idx] === val;
          });
        });

        if (matched && variantInput) {
          variantInput.value = matched.id;

          // Update price display
          const priceEl = document.querySelector('.product-info__price');
          if (priceEl && matched.price) {
            const formatted = (matched.price / 100).toFixed(2);
            priceEl.textContent = `$${formatted}`;
          }

          // Update availability
          const submitBtn = document.querySelector('.product-form__submit');
          if (submitBtn) {
            submitBtn.disabled = !matched.available;
            submitBtn.textContent = matched.available ? 'ADD TO CART' : 'SOLD OUT';
          }
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     CART PAGE: Quantity updates
  ───────────────────────────────────────────── */
  document.querySelectorAll('.cart-item__qty').forEach((qtyWrap) => {
    const minusBtn = qtyWrap.querySelector('[data-minus]');
    const plusBtn = qtyWrap.querySelector('[data-plus]');
    const input = qtyWrap.querySelector('input');

    if (!input) return;

    function updateCartItem(key, qty) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      })
        .then((r) => r.json())
        .then((cart) => {
          updateCartCount(cart.item_count);
          // Update subtotal
          const subtotalEl = document.querySelector('[data-cart-subtotal]');
          if (subtotalEl) {
            subtotalEl.textContent = `$${(cart.total_price / 100).toFixed(2)}`;
          }
          if (qty === 0) {
            const cartItem = qtyWrap.closest('.cart-item');
            if (cartItem) cartItem.remove();
          }
        });
    }

    if (minusBtn) {
      minusBtn.addEventListener('click', () => {
        const key = input.dataset.key;
        const current = parseInt(input.value, 10);
        const newQty = Math.max(0, current - 1);
        input.value = newQty;
        updateCartItem(key, newQty);
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', () => {
        const key = input.dataset.key;
        const current = parseInt(input.value, 10);
        const newQty = current + 1;
        input.value = newQty;
        updateCartItem(key, newQty);
      });
    }
  });

  // Cart remove buttons
  document.querySelectorAll('[data-remove-key]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.removeKey;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      })
        .then((r) => r.json())
        .then((cart) => {
          updateCartCount(cart.item_count);
          const cartItem = btn.closest('.cart-item');
          if (cartItem) cartItem.remove();
          const subtotalEl = document.querySelector('[data-cart-subtotal]');
          if (subtotalEl) {
            subtotalEl.textContent = `$${(cart.total_price / 100).toFixed(2)}`;
          }
          if (cart.item_count === 0) {
            window.location.reload();
          }
        });
    });
  });

  /* ─────────────────────────────────────────────
     COLLECTION FILTERS
  ───────────────────────────────────────────── */
  const filterBtns = document.querySelectorAll('.filter-btn');

  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      filterBtns.forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const tag = btn.dataset.tag;
      const cards = document.querySelectorAll('.product-card-wrap');

      cards.forEach((card) => {
        if (!tag || tag === 'all') {
          card.style.display = '';
        } else {
          const cardTags = card.dataset.tags || '';
          card.style.display = cardTags.includes(tag) ? '' : 'none';
        }
      });
    });
  });

  /* ─────────────────────────────────────────────
     NEWSLETTER FORM
  ───────────────────────────────────────────── */
  document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const emailInput = form.querySelector('input[type="email"]');
      const note = form.querySelector('.form-note');

      if (!emailInput || !emailInput.value) return;

      // Shopify handles form submission natively via action="/contact#newsletter"
      // For AJAX, we use this:
      form.submit();
    });
  });

  /* ─────────────────────────────────────────────
     LOAD MORE — Collection Grid
  ───────────────────────────────────────────── */
  const loadMoreBtn = document.getElementById('load-more-btn');

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', () => {
      const nextPage = parseInt(loadMoreBtn.dataset.nextPage || '2', 10);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('page', nextPage);

      loadMoreBtn.textContent = 'LOADING...';
      loadMoreBtn.disabled = true;

      fetch(currentUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newCards = doc.querySelectorAll('.product-card-wrap');
          const grid = document.querySelector('.products-grid');

          if (grid && newCards.length) {
            newCards.forEach((card) => grid.appendChild(card));

            // Re-attach quick add events
            grid.querySelectorAll('[data-quick-add]').forEach((btn) => {
              btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                addToCart(btn.dataset.variantId, 1, btn);
              });
            });

            loadMoreBtn.dataset.nextPage = nextPage + 1;
            loadMoreBtn.textContent = 'LOAD MORE';
            loadMoreBtn.disabled = false;

            const newLoadMore = doc.getElementById('load-more-btn');
            if (!newLoadMore) loadMoreBtn.style.display = 'none';
          } else {
            loadMoreBtn.style.display = 'none';
          }
        });
    });
  }

  /* ─────────────────────────────────────────────
     INIT: Update cart count on page load
  ───────────────────────────────────────────── */
  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      updateCartCount(cart.item_count);
    })
    .catch(() => {});

})();
